import type { MembershipSummary } from '../identity/types';

/**
 * EPN-36 — the one place that turns the backend's raw per-membership facts
 * (`isPartnerChannel`/`companyIsActive`) into a Partner Sign In routing decision. The backend never makes
 * this policy call itself (see `MembershipSummary`'s own doc comment on the backend) — it is deliberately
 * owned here, in the frontend, so it can change without touching the shared auth contract every other
 * screen in the app also reads.
 *
 * - `'eligible'` — at least one membership is both Partner-channel and active. Routes into the existing
 *   Admin Portal exactly like any other successful sign-in; there is no separate Partner dashboard to
 *   send them to (see docs/epn-36-partner-signin-gaps.md).
 * - `'inactive'` — every Partner-channel membership this account has is on a deactivated company, and it
 *   has no OTHER (non-Partner) membership either. Blocked, with an honest message — see
 *   PartnerAccountInactivePage.
 * - `'notPartner'` — valid credentials, but no Partner-channel membership exists at all. Treated as
 *   "wrong front door," not a security event: this account still proceeds into its own (Admin-channel)
 *   portal exactly as if they'd used the regular Sign In page — see PartnerLoginPage's own doc comment for
 *   why a hard block here would be the wrong default.
 */
export type PartnerEligibility = 'eligible' | 'inactive' | 'notPartner';

export function evaluatePartnerEligibility(memberships: readonly MembershipSummary[]): PartnerEligibility {
  const partnerMemberships = memberships.filter((m) => m.isPartnerChannel === true);
  if (partnerMemberships.length === 0) {
    return 'notPartner';
  }
  // A membership missing `companyIsActive` (only ever happens in a test/mock fixture — the real backend
  // always sends it) is treated as active, same as the backend's own default.
  const hasActivePartnerMembership = partnerMemberships.some((m) => m.companyIsActive !== false);
  return hasActivePartnerMembership ? 'eligible' : 'inactive';
}
